import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import axios from "axios"
import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Textarea } from "design-system/atoms/Textarea"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CreateVaultEntrySchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { maskedInputClassName, noAutofillProps } from "../../lib/vaultInput"
import { vaultQuery } from "../../lib/vaultQuery"

type FieldValues = z.infer<typeof CreateVaultEntrySchema>

export function CreateVaultEntry() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm<FieldValues>({
    resolver: zodResolver(CreateVaultEntrySchema),
    defaultValues: { key: "", value: "", isSecret: true },
  })

  const isSecret = form.watch("isSecret")

  const { mutate, isPending } = useMutation({
    mutationFn: async (values: FieldValues) => {
      await axios.post("/vault", values)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: vaultQuery().queryKey })
      toast.success(t("settings.keysAndSecrets.vault.created"))
      onClose()
    },
  })

  function onClose() {
    navigate({ to: "/settings/keys-and-secrets" })
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[475px]">
        <DialogHeader>
          <DialogTitle>{t("settings.keysAndSecrets.vault.createTitle")}</DialogTitle>
          <DialogDescription>
            {t("settings.keysAndSecrets.vault.createDescription")}
          </DialogDescription>
        </DialogHeader>
        <Form
          {...form}
          errorTranslations={getErrorTranslations()}
          onSubmit={form.handleSubmit((values) => mutate(values))}
        >
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("settings.keysAndSecrets.vault.key")}</FormLabel>
                  <FormControl>
                    <Input {...field} {...noAutofillProps} autoFocus placeholder="MY_KEY" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("settings.keysAndSecrets.vault.value")}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      {...noAutofillProps}
                      className={isSecret ? maskedInputClassName : undefined}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isSecret"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                  </FormControl>
                  <FormLabel className="mb-0">
                    {t("settings.keysAndSecrets.vault.secretHint")}
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t("shared.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {t("settings.keysAndSecrets.vault.add")}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
